/* ==========================================================================
   STARKFELD cart redesign (C2, 2026-08-28)

   Three jobs, all of them defensive because <main-cart> replaces its own
   innerHTML on every cart update (assets/cart.js, onCartUpdate):

   1. Sticky bottom bar: appears only once the main checkout CTA has left the
      viewport, disappears again when it returns (IntersectionObserver).
   2. Delivery date: Liquid renders a server-side estimate in the shop time
      zone; this corrects it to the visitor's local clock (13:00 cutoff,
      +2 working days, weekends skipped, public holidays deliberately ignored).
   3. Discount code: field stays closed until the shopper asks for it.

   Everything re-initialises through a MutationObserver rather than the theme's
   pubsub, so it survives every re-render path including the ones that do not
   publish a cartUpdate event.
   ========================================================================== */
(function () {
  'use strict';

  var DOCK_VISIBLE = 'is-visible';

  /* --- delivery date ----------------------------------------------------- */

  function addWorkingDays(from, days) {
    var d = new Date(from.getTime());
    var added = 0;
    var guard = 0;
    while (added < days && guard < 40) {
      d.setDate(d.getDate() + 1);
      var wd = d.getDay();
      if (wd !== 0 && wd !== 6) added++;
      guard++;
    }
    return d;
  }

  function formatDate(node, date) {
    var weekdays = (node.getAttribute('data-weekdays') || '').split(',');
    var months = (node.getAttribute('data-months') || '').split(',');
    var pattern = node.getAttribute('data-format') || '';
    if (weekdays.length !== 7 || months.length !== 12 || !pattern) return null;

    /* Word order is language specific ("Di, 2. Sep." vs "Tue, Sep 2"), so the
       pattern comes from the locale file, exactly as it does in Liquid. */
    return pattern
      .replace('[weekday]', weekdays[date.getDay()])
      .replace('[day]', String(date.getDate()))
      .replace('[month]', months[date.getMonth()]);
  }

  function refreshDeliveryDates(root) {
    var nodes = root.querySelectorAll('[data-sf-delivery-date]');
    if (!nodes.length) return;

    var now = new Date();
    var cutoff = parseInt(nodes[0].getAttribute('data-cutoff') || '13', 10);
    var base = parseInt(nodes[0].getAttribute('data-days') || '2', 10);

    /* Past the cutoff the order leaves the warehouse a day later. */
    var days = now.getHours() >= cutoff ? base + 1 : base;
    var target = addWorkingDays(now, days);

    for (var i = 0; i < nodes.length; i++) {
      var text = formatDate(nodes[i], target);
      if (text) nodes[i].textContent = text;
    }
  }

  /* --- sticky bottom bar -------------------------------------------------- */

  var observer = null;
  var observedCta = null;

  function syncDockTotal(root, dock) {
    var source = root.querySelector('[data-sf-total-value]');
    var target = dock.querySelector('[data-sf-dock-value]');
    if (source && target) target.textContent = source.textContent.trim();
  }

  function setupDock(root) {
    var dock = document.querySelector('[data-sf-cart-dock]');
    if (!dock) return;

    var cta = root.querySelector('[data-sf-primary-cta]');

    syncDockTotal(root, dock);

    if (!cta) {
      dock.classList.remove(DOCK_VISIBLE);
      return;
    }

    /* Nothing to re-wire when the very same node is already being watched. */
    if (observedCta === cta && observer) return;

    if (observer) observer.disconnect();
    observedCta = cta;

    if (!('IntersectionObserver' in window)) {
      /* Without the API the bar would never appear; leave it hidden rather
         than permanently covering the page. */
      dock.classList.remove(DOCK_VISIBLE);
      return;
    }

    observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) dock.classList.remove(DOCK_VISIBLE);
        else dock.classList.add(DOCK_VISIBLE);
      });
    }, { root: null, threshold: 0 });

    observer.observe(cta);
  }

  /* --- discount code toggle ------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest && event.target.closest('[data-sf-coupon-toggle]');
    if (!toggle) return;
    event.preventDefault();

    var wrap = toggle.closest('[data-sf-coupon]');
    if (!wrap) return;
    var panel = wrap.querySelector('[data-sf-coupon-panel]');
    if (!panel) return;

    var open = !panel.hidden;
    panel.hidden = open;
    toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    if (!open) {
      var input = panel.querySelector('input[name="discount"]');
      if (input) input.focus();
    }
  });

  /* --- cross-sell add to cart ---------------------------------------------- */

  document.addEventListener('click', function (event) {
    var btn = event.target.closest && event.target.closest('[data-sf-xsell-add]');
    if (!btn) return;
    event.preventDefault();

    var id = btn.getAttribute('data-variant-id');
    if (!id || btn.hasAttribute('disabled')) return;

    btn.setAttribute('disabled', 'disabled');

    fetch((window.theme && theme.routes && theme.routes.cart_add_url) || '/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
    })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        /* A sold-out variant is refused by Shopify. Reload either way so the
           cart, its totals and every automatic discount come straight from the
           server instead of being guessed here. */
        if (data && data.status && data.status !== 200) {
          btn.removeAttribute('disabled');
          return;
        }
        window.location.href = (window.theme && theme.routes && theme.routes.cart_url) || '/cart';
      })
      .catch(function () { btn.removeAttribute('disabled'); });
  });

  /* --- boot + re-init ------------------------------------------------------ */

  function init() {
    var root = document.querySelector('main-cart') || document;
    refreshDeliveryDates(root);
    setupDock(root);
  }

  function boot() {
    init();

    var host = document.querySelector('main-cart');
    if (!host || !('MutationObserver' in window)) return;

    var pending = null;
    new MutationObserver(function () {
      if (pending) clearTimeout(pending);
      pending = setTimeout(init, 60);
    }).observe(host, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
